import type { DragEndEvent } from "@dnd-kit/core/dist/types/events"
import { arrayMove } from "@dnd-kit/sortable"
import { Card, CardStatusEnum, Employee, Expense, type FilterPresetEnum, Transaction, TransactionCategory, Vendor, Workflow } from "@finway-group/shared/lib/models"
import axios from "axios"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { convertSortingToString } from "~Shared/queries/query.utils"
import { TableService } from "~Shared/services"
import { store } from "~Shared/store"
import { fetchTable } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { type Table, type TableData, type TransactionCategoryWithChildren, createInitialTable } from "~Shared/store/reducers/tableReducer"
import { type RootState } from "~Shared/store/rootState.interface"
import { employeeSorter, insertIf } from "~Shared/utils/helper.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { TableDocType, getTableDefaultSort, getTableDocType } from "~Shared/utils/table.utils"

/*
SELECTORS (STORE SUBSCRIPTION)
*/
export const useTable = <T>(table: TablesEnum): Table<T> => {
    const storeTable: Table<T> = useSelector((({ tables }: RootState) => tables[table]) as any)

    // if more specific differentiation is needed in the future, use TableEnum instead of TableDocType
    const tableDocType = getTableDocType(table)
    switch (tableDocType) {
        case TableDocType.INBOX_INVOICE:
            return { ...storeTable, data: { ...storeTable.data, docs: storeTable.data.docs } }
        case TableDocType.EXPENSE:
        case TableDocType.TRIP_FOLDER:
            return {
                ...storeTable,
                data: {
                    ...storeTable.data,
                    docs: storeTable.data.docs.map((expense: any) =>
                        expense?.children?.length > 0 ? new Expense({ ...expense, children: expense.children.map((child: any) => new Expense(child)) }) : new Expense(expense),
                    ),
                },
            } as any
        case TableDocType.CARD:
            return {
                ...storeTable,
                data: {
                    ...storeTable.data,
                    ...insertIf(!PROCESS_ENV.IS_DEMO, { filter: { status: CardStatusEnum.ACTIVE } }),
                    docs: storeTable.data.docs.map((card) => new Card(card)),
                },
            } as any
        case TableDocType.TRANSACTION:
            return { ...storeTable, data: { ...storeTable.data, docs: storeTable.data.docs.map((transaction) => new Transaction(transaction)) } } as any
        case TableDocType.EMPLOYEE:
            return { ...storeTable, data: { ...storeTable.data, docs: storeTable.data.docs.map((employee) => new Employee(employee)).sort(employeeSorter) } } as any
        case TableDocType.COST_CENTER:
        case TableDocType.EXPORT_HISTORY:
            return storeTable
        case TableDocType.VENDOR:
            return { ...storeTable, data: { ...storeTable.data, docs: storeTable.data.docs.map((vendor) => new Vendor(vendor)) } } as any
        case TableDocType.WORKFLOW:
            return { ...storeTable, data: { ...storeTable.data, docs: storeTable.data.docs.map((workflow) => new Workflow(workflow)) } } as any
        case TableDocType.TRANSACTION_CATEGORY:
            return {
                ...storeTable,
                data: {
                    ...storeTable.data,
                    docs: storeTable.data.docs.map((doc: any) => {
                        const transactionCategory = new TransactionCategory(doc) as unknown as TransactionCategoryWithChildren
                        transactionCategory.children = transactionCategory.children.map((child: any) => new TransactionCategory(child))
                        return transactionCategory
                    }),
                },
            } as any
        default:
            return createInitialTable()
    }
}

export const useTableShouldFetch = (table: TablesEnum): boolean => useSelector(({ tables }: RootState) => tables[table].shouldFetch)

export const useTableFilterObject = (table: TablesEnum) => useSelector(({ tables }: RootState) => tables[table].data.filter)

export const useTableFilterPresets = (table: TablesEnum) => useSelector(({ tables }: RootState) => (tables[table].data.filter?.presets ?? []) as Array<FilterPresetEnum>)

export const useTableFilterQueryString = (table: TablesEnum) => {
    const filterObject = useSelector(({ tables }: RootState) => tables[table].data.filter)
    return TableService.getTableFilterQueryString(table, filterObject)
}

export const useTableSearchString = (table: TablesEnum) => useSelector(({ tables }: RootState) => tables[table].data.search)

/*
STORE ACCESS
*/
export const getTableDocsStore = (table: TablesEnum) => store.getState().tables[table].data.docs

export const getTableFilterObjectStore = <T>(table: TablesEnum) => {
    const data = store.getState().tables[table].data.filter
    return data as T
}

export const getTableFilterQueryStringStore = (table: TablesEnum) => {
    const filterObject = store.getState().tables[table].data.filter
    return TableService.getTableFilterQueryString(table, filterObject)
}

export const getTableSearchQueryStringStore = (table: TablesEnum) => {
    const searchString = store.getState().tables[table].data.search
    return TableService.getTableSearchQueryString(table, searchString)
}

export const getTableQueryParams = async (table: TablesEnum) => {
    const { filter, sort, limit, page, search } = store.getState().tables[table].data as TableData<any>

    return {
        limit,
        page,
        search,
        filterQueryString: TableService.getTableBaseFilterQueryString(table) + TableService.getTableFilterQueryString(table, filter),
        sortQueryString: sort.field ? convertSortingToString(sort) : convertSortingToString(getTableDefaultSort(table)),
        searchQueryString: TableService.getTableSearchQueryString(table, search),
    }
}

export const getTableQueryStringWithoutPagination = (table: TablesEnum) => {
    const { filter, search } = store.getState().tables[table].data as TableData<any>
    return `${TableService.getTableBaseFilterQueryString(table) + TableService.getTableFilterQueryString(table, filter)}${TableService.getTableSearchQueryString(table, search)}`
}

/*
OTHER HOOKS
*/
export interface tableOptions {
    cardId?: string
    expenseId?: string
    inboxInvoiceId?: string
}

export const useFetchTable = (table: TablesEnum, options: tableOptions = {}) => {
    const dispatch = useDispatch()
    const shouldFetch = useTableShouldFetch(table) // checks if table is in redux

    useEffect(() => {
        if (!shouldFetch) {
            return
        }

        const source = axios.CancelToken.source()
        dispatch(fetchTable(table, source, options))

        return () => {
            source.cancel("Cancelling in cleanup")
        }
    }, [shouldFetch])
}

export const useTableFilter = (table: TablesEnum) => useSelector(({ tables }: RootState) => tables[table].data.filter)

/**
 * Triggers a loading when dashboard search or filter change, until the table passed as parameter is fetched or returns an error
 */
export const useTableFilterAndSearchLoading = (table: TablesEnum) => {
    const storeTable: Table<any> = useTable(table)
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const tableSearchString = useTableSearchString(table)
    const tableFilterQueryString = useTableFilterQueryString(table)

    useEffect(() => {
        if (storeTable.shouldFetch) {
            setIsLoading(true)
        } // When we mount the tab, if it has to fetch the table we set the loading to true until it fetches it.
    }, [tableSearchString, tableFilterQueryString])

    useEffect(() => {
        if ((storeTable.isFetching === false || storeTable.error) && isLoading === true) {
            setIsLoading(false)
        }
    }, [storeTable.isFetching, storeTable.error, isLoading])

    return isLoading
}

export const useTodoFilterAndSearchTabLoading = () => {
    const purchaseApprovalLoading = useTableFilterAndSearchLoading(TablesEnum.TODO_PURCHASE_APPROVAL)
    const invoiceApprovalLoading = useTableFilterAndSearchLoading(TablesEnum.TODO_INVOICE_APPROVAL)
    const docsNeededLoading = useTableFilterAndSearchLoading(TablesEnum.TODO_DOCS_NEEDED)
    const approvalPendingLoading = useTableFilterAndSearchLoading(TablesEnum.TODO_APPROVAL_PENDING)
    return purchaseApprovalLoading || invoiceApprovalLoading || docsNeededLoading || approvalPendingLoading
}

export const usePayAndExportFilterAndSearchTabLoading = () => {
    const toBePaid = useTableFilterAndSearchLoading(TablesEnum.PAY_AND_EXPORT_TO_BE_PAID)
    const toBeExported = useTableFilterAndSearchLoading(TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED)
    return toBePaid || toBeExported
}

type DraggableTableParams<T> = {
    dataSource: Array<T>
    handleDragEnd: ({ activeIndex, overIndex, newList }: { activeIndex: number; overIndex: number; newList: Array<T> }) => Promise<void> | void
}

export interface HandleDragEndParams<T> {
    newList: Array<DraggableTableDataSourceType<T>>
    overIndex: number
    activeIndex: number
}

export type DraggableTableDataSourceType<T> = T & { key: string; _id?: string; children?: Array<T> }

export const useDraggableTable = <T>({ dataSource, handleDragEnd }: DraggableTableParams<T>) => {
    const addKeyToData = (dataSource: Array<T>) =>
        dataSource.map<DraggableTableDataSourceType<T>>((obj: DraggableTableDataSourceType<T>, index: number) => ({
            ...obj,
            key: obj._id || obj.key.toString() || index.toString(),
        }))

    const dataSourceWithKeys = addKeyToData(dataSource)
    const [draggableTempData, setDraggableTempData] = useStateIfMounted<Array<DraggableTableDataSourceType<T>>>([])
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [activeId, setActiveId] = useStateIfMounted(null)

    const onDragStart = (event: any) => {
        setActiveId(event.active.id)
    }

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (active.id === over?.id) {
            return
        }
        const activeIndex = dataSourceWithKeys.findIndex((item) => item.key === active.id)
        const overIndex = dataSourceWithKeys.findIndex((item) => item.key === over?.id)
        const newList = arrayMove(dataSourceWithKeys, activeIndex, overIndex)
        setIsLoading(true)
        setDraggableTempData(newList)

        Promise.resolve(handleDragEnd({ activeIndex, overIndex, newList })).finally(() => {
            setIsLoading(false)
            setActiveId(null)
            setDraggableTempData([])
        })
    }

    return { dataSource: isLoading ? draggableTempData : dataSourceWithKeys, onDragEnd, onDragStart, isLoading, activeId }
}
